import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Clock, Trash2, Hash } from "lucide-react";
import { toast } from "sonner";
import {
  getGuestHistory,
  addGuestHistory,
  removeGuestHistory,
} from "@/lib/playerHistory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const cleanTag = (t) =>
  (t || "").trim().toUpperCase().replace(/^#/, "").replace(/\s+/g, "");

export default function PlayersPage() {
  const navigate = useNavigate();
  const [tag, setTag] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setHistory(getGuestHistory());
  }, []);

  const goTag = useCallback(
    (t) => {
      const c = cleanTag(t);
      if (!c) return;
      setHistory(addGuestHistory(c));
      navigate(`/player/${c}`);
    },
    [navigate],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const c = cleanTag(tag);
    if (!c) {
      toast.error("Oyuncu tag'ı girin");
      return;
    }
    setLoading(true);
    setTimeout(() => setLoading(false), 400);
    goTag(c);
  };

  const removeFromHistory = (t, e) => {
    e.stopPropagation();
    setHistory(removeGuestHistory(t));
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <img
            src={`${process.env.PUBLIC_URL || ""}/site-icon.png`}
            alt="SalihiCr Stats"
            className="h-12 w-12 sm:h-14 sm:w-14 object-contain drop-shadow-[0_2px_8px_rgba(255,200,80,0.35)]"
          />
          <div>
            <div className="font-display text-2xl sm:text-3xl tracking-wide leading-none flex items-baseline gap-1.5">
              <span>SalihiCr Stats</span>
              <span className="text-[10px] uppercase tracking-[0.15em] text-amber-300/90 font-semibold border border-amber-300/40 rounded px-1 py-0.5 leading-none">
                beta
              </span>
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
              Clash Royale · Oyuncu İstatistikleri
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 space-y-6">
        <section className="card-surface rounded-2xl border border-white/10 p-4 sm:p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
            Oyuncu Tag&apos;ı ile Ara
          </h2>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="player-tag-input"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="Oyuncu tag'ı (ör. VPVQLCVRC veya #VPVQLCVRC)"
                className="pl-9 bg-background/60 border-white/15 uppercase"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              data-testid="player-go-button"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:brightness-110"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              <span className="ml-1.5">Git</span>
            </Button>
          </form>
        </section>

        {history.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-sky-300" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Daha Önce Arattıkların · {history.length}
              </h2>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="history-list">
              {history.map((s) => (
                <li
                  key={s.tag}
                  data-testid={`history-item-${s.tag.replace("#", "")}`}
                  onClick={() => goTag(s.tag)}
                  className="group card-surface rounded-xl border border-white/10 px-4 py-3 flex items-center justify-between gap-2 cursor-pointer hover:border-primary/40 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-semibold truncate">
                      {s.name || s.tag}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {s.tag}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => removeFromHistory(s.tag, e)}
                      title="Geçmişten çıkar"
                      className="p-2 rounded-md text-muted-foreground hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
